import * as events from '../../util/event-bus';
import * as store from '../../util/store';
import * as env from '../../util/env';
import * as dom from '../../util/easy-dom';
import { glean } from '../../util/object-helpers';
import { getBaseUrlForModule } from '../../util/baseurl-helper';

let httpGet = null;
let passportData = null;
let idmData = null;
let featureFlagsCC = null;
let i18n = null;
let elem = null;
let skipSidekickPermissionsApps = '';

// get the token and passportData from the sidekick, store in local vars
events.once('token-ready', ({ get }) => { httpGet = get; makeMorpheusPlatformNav(); }, { greedy: true });
events.once('feature-flags-ready-cc',
	(flagsSvc) => {
		featureFlagsCC = flagsSvc;
        // Fetching applications that should bypass Sidekick permission checks
		Promise.all([featureFlagsCC.getValue('skip_sidekick_permissions')]).then((apps) => {
            skipSidekickPermissionsApps = apps[0];
            makeMorpheusPlatformNav();
        });
	},
	{ greedy: true }
);
store.onValueOnce('passport-data', _passportData_ => { passportData = _passportData_; makeMorpheusPlatformNav(); });
store.onValueOnce('idm-data', _idmData_ => { idmData = _idmData_; makeMorpheusPlatformNav(); });
// wait for nav component to send us the translations service instance
export default (_elem, _i18n) => { i18n = _i18n; elem = _elem; makeMorpheusPlatformNav(); };

// called multiple times but doesn't truly execute until all data is available (like Promise.all())
function makeMorpheusPlatformNav() {
    // if we don't have all the data we need yet, wait till we do
    const skConfig = store.get('sk-config');
    const { config } = skConfig || {};
    const { endpoint } = config || {};
    // if an endpoint is configured, use the new flow and skip building legacy navigation items
    if (endpoint) { return; }
    if (idmData !== null) { return; }
    if (httpGet == null) { return; }
    if (featureFlagsCC == null) { return; }
    if (i18n == null) { return; }
    if (passportData == null) { return; }

    events.on('_location.path-change', () => dom.rAF(() => {
        const platformHasHighlight = elem.find('.sk--platform-menu.highlight').length > 0;
        elem.toggleClass('sk--platform-has-highlight', platformHasHighlight);
    }));

    // locate environment specific urls
    let envName = env.environmentFromPassportDomain(window.location.host);
    const envValues = env[envName];
    const { dataExporterBaseUrl, rubricDesignerUrl } = envValues;
    const { legacyFormattedData: { productId }, userInfo: { role: userRole } } = passportData;
    let appBaseUrl = `${env.getJarvisServiceMap(productId, envValues.jarvisServiceMap)}` || '';
    const pId = env.productIdFromMap(productId, envValues.productIdMap);

    // Check if the current application is configured to bypass Sidekick permissions for menu items
    const skipSidekickPermissions = skipSidekickPermissionsApps
        ? skipSidekickPermissionsApps.toLowerCase()
            .replace(/\s/g, '')
            .split(',')
            .includes(productId.toLowerCase())
        : false;

    // Exit early if you don't want to bypass permissions for native Morpheus apps
    // Note: This might need to be updated later for apps fully adopting native Morpheus
    if (!skipSidekickPermissions) { return; }

    // the full extended nav (pre-permissions)
    const platformNavAll = [
        {
            title: i18n('System Configuration'),
            icon: 'fa-li-settings-sliders',
            routeMatcher: [`^/${pId}-useraccess`],
            children: [],
        }, {
            title: i18n('Data Exporter'),
            icon: 'fa-nav-cloud-download',
            url: `${dataExporterBaseUrl}/${pId}/list/`,
            routeMatcher: [`${dataExporterBaseUrl}/${pId}/.*$`],
            visible: ({ flagsCC }) => {
                return flagsCC.checkState('plat_data_exporter').then(() => {
                    return false;
                });
            }
        }, {
            title: i18n('Resource Library'),
            icon: 'fa-li-hand-papers',
            routeMatcher: [`^/${pId}-resource-library`],
            visible: ({ flagsCC }) => flagsCC.checkState('sk_resource_library_sidenav_update'),
            children: [
                {
                    title: i18n('Library'),
                    children: [
                        {
                            title: i18n('Browse Library'),
                            routeMatcher: [`^/${pId}-resource-library/library`],
                            url: `${appBaseUrl}/${pId}-resource-library/library`,
                        },
                        {
                            title: i18n('My Resource History'),
                            routeMatcher: [`^/${pId}-resource-library/history`],
                            url: `${appBaseUrl}/${pId}-resource-library/history`,
                        }
                    ]
                },
                {
                    title: i18n('Administration'),
                    children: [
                        {
                            title: i18n('My Organization Resources'),
                            routeMatcher: [`^/${pId}-resource-library/my-org-resources`],
                            url: `${appBaseUrl}/${pId}-resource-library/my-org-resources`,
                        },
                        {
                            title: i18n('My Resources'),
                            routeMatcher: [`^/${pId}-resource-library/my-resources`],
                            url: `${appBaseUrl}/${pId}-resource-library/my-resources`
                        },
                        {
                            title: i18n('Collaborative Video Rubrics'),
                           routeMatcher: [`${getBaseUrlForModule(rubricDesignerUrl, '-rubric')}/?productId=${pId}`],
							url: `${getBaseUrlForModule(rubricDesignerUrl, '-rubric')}/?productId=${pId}`
                        },
                        {
                            title: i18n('Learning Designer Admin'),
                            routeMatcher: [`^/${pId}-resource-library/admin/learning-designer`],
                            url: `${appBaseUrl}/${pId}-resource-library/admin/learning-designer`
                        },
                        {
                            title: i18n('Reports'),
                            routeMatcher: [`^/${pId}-resource-library/admin/reports`],
                            url: `${appBaseUrl}/${pId}-resource-library/admin/reports`
                        },
                        {
                            title: i18n('Manage Content Curator'),
                            routeMatcher: [`^/${pId}-resource-library/manage-privileges`],
                            url: `${appBaseUrl}/${pId}-resource-library/manage-privileges`,
                        }
                    ]
                }
            ]
        },
         {
            title: i18n('Core Configuration'),
            icon: 'fa-cogs-light',
            routeMatcher: [`^/${pId}-settings/permissions/profiles/all`, `^/${pId}-settings/hcm-core-config/*`],
            visible: ({ flagsCC }) => {
                return flagsCC.checkState('hcm_core_configuration').then(coreConfigFlagStatus => coreConfigFlagStatus);
            },
            children: [
                {
                    title: i18n('Organization & Access'),
                    children: [
                        {
                            title: i18n('HCM Permissions'),
                            routeMatcher: [`^/${pId}-settings/permissions/.*$`],
                            url: `${appBaseUrl}/${pId}-settings/permissions/profiles/all`,
                        }
                    ]
                }, {
                    title: i18n('HCM Core Configuration'),
                    children: [
                        {
                            title: i18n('Bargaining Units'),
                            routeMatcher: [`^/${pId}-settings/hcm-core-config/bargaining-units(/.*)?$`],
                            url: `${appBaseUrl}/${pId}-settings/hcm-core-config/bargaining-units`,
                        },
                        {
                            title: i18n('Benefits'),
                            routeMatcher: [`^/${pId}-settings/hcm-core-config/benefits(/.*)?$`],
                            url: `${appBaseUrl}/${pId}-settings/hcm-core-config/benefits`,
                        },
                        {
                            title: i18n('Departments'),
                            routeMatcher: [`^/${pId}-settings/hcm-core-config/departments$`],
                            url: `${appBaseUrl}/${pId}-settings/hcm-core-config/departments`,
                        },
                        {
                            title: i18n('Job Definition'),
                            routeMatcher: [`^/${pId}-settings/hcm-core-config/job-definition(/.*)?$`],
                            url: `${appBaseUrl}/${pId}-settings/hcm-core-config/job-definition`,
                        },
                        {
                            title: i18n('Sites/Locations'),
                            routeMatcher: [`^/${pId}-settings/hcm-core-config/sites$`],
                            url: `${appBaseUrl}/${pId}-settings/hcm-core-config/sites`,
                        }
                    ]

                }
            ]
        }
    ];

    let employee;
    if (env.MY_STAFF_PROFILE_ALLOWED_ROLES.includes(userRole.name) || skipSidekickPermissions) {
        employee = userRole;
    }

    // If the user is an employee, we need to add the Employee Profile or Staff Directory menu item
    const showStaffDirectory = env.STAFF_DIRECTORY_ALLOWED_ROLES.includes(userRole.name);
    const showEmployeeProfile = employee && !showStaffDirectory;

    // Add the Staff Directory or Employee Profile menu item based on the user's role
    if (showStaffDirectory) {
        platformNavAll.unshift({
            title: i18n('Staff Directory'),
            icon: 'fa-nav-users',
            routeMatcher: [`^\/${pId}-employee\/(?!(?:reports)).*$`],
            url: `${appBaseUrl}/${pId}-employee/`,
            visible: ({ flagsCC }) => flagsCC.checkState('global_fc_shared_employee')
        });
    } else if (showEmployeeProfile) {
        platformNavAll.unshift({
            title: i18n('My Staff Profile'),
            icon: 'fa-nav-user-circle',
            routeMatcher: [`^/${pId}-employee/record/.*$`],
            url: `${appBaseUrl}/${pId}-employee/record/${employee.id}/personal`,
            visible: ({ flagsCC }) => flagsCC.checkState('global_fc_shared_employee')
        });
    }

    // An array to hold the results of checkState promise along with the menu item associated with it
    const visiblePromises = [];

    // Resolves the checkState functions by calling item.visible recursively and storing each promise
    // with the item in an object into the visiblePromises array above
    const resolveMenuAccess = menu => {
        menu.forEach((item, idx) => {
            if (item.visible) {
                const visiblePromise = item.visible({
                    flagsCC: featureFlagsCC
                });

                visiblePromises.push({
                    item,
                    visiblePromise
                });
            }

            if (item.children) {
                resolveMenuAccess(item.children);
            }
        });
    };

        // remove everything this user doesn't have access to, using the hasVisibility that was set
        // to the resolved value of the checkState promise
    const sanitizeMenu = menu => {
        menu.forEach((item, idx) => {
            // if this item has a permission function that returns false (no-access) we
            // should remove the item, otherwise, leave it alone
            const hasAccessToItem = glean(item, i => (i.visible == null || i.hasVisibility === true), false);

            if (!hasAccessToItem) {
                delete menu[idx];
            } else if (item.children) {
                // if this item has children, sanitize the submenu
                item.children = sanitizeMenu(item.children);

                    // if all the children have been removed, there's no need to keep the parent
                if (item.children.length === 0) {
                    delete menu[idx];
                }
            }
        });

        // return the menu with deleted items removed
        return menu.filter(item => item !== undefined);
    };

    // Gather all of the promises
    resolveMenuAccess(platformNavAll);

    // Wait until all checkState promises have resolved
    Promise.all(visiblePromises.map(vp => vp.visiblePromise)).then((visibleSettings) => {
        // For each item that had a promise, set hasVisibility to the resolved value of the checkState promise
        visiblePromises.forEach((vp, idx) => {
            vp.item.hasVisibility = visibleSettings[idx];
        });
    }).then(() => {
        // Finally sanitize the menu and set it to the store
        const personalizedNav = sanitizeMenu(platformNavAll);
        store.set('below-line-nav', personalizedNav);
    });
}
